import React, { useState } from "react";
import {
  X,
  Download,
  Calendar,
  Wallet,
  TrendingUp,
  MapPin,
  CheckCircle,
  FileText,
  ChevronRight,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import { useAppContext } from "../../../AppContext";
import moment from "moment";
import * as XLSX from "xlsx";

export default function AccountantContractorEmployeesModalWorkSlip({
  visible,
  onClose,
  employee,
  contractorName,
  places,
  dailyWork,
  allEmployees,
}) {
  const { t } = useTranslation();
  const { Lang } = useAppContext();
  const isRTL = Lang === "ar" || Lang === "he";
  const [selectedMonth, setSelectedMonth] = useState(null);
  const [isExporting, setIsExporting] = useState(false);
  const [exportModalVisible, setExportModalVisible] = useState(false);

  if (!employee || !visible) return null;

  // Determine if employee has monthly salary
  const isMonthlyEmployee = employee?.typeSalary === 2;
  const employeeSalary = isMonthlyEmployee
    ? employee?.monthlySalary || 0
    : employee?.salary || 0;

  // Generate last 12 months
  const generateMonths = () => {
    const months = [];
    const now = new Date();
    for (let i = 0; i < 12; i++) {
      const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
      const m = String(d.getMonth() + 1).padStart(2, "0");
      const y = d.getFullYear();
      months.push(`${m}-${y}`);
    }
    return months;
  };

  const monthList = generateMonths();

  // Set default month to current month if not selected
  if (!selectedMonth && monthList.length > 0) {
    setSelectedMonth(monthList[0]);
  }

  // Filter daily work by employee and selected month
  const filteredDailyWork = dailyWork.filter((item) => {
    if (!employee?.id || !selectedMonth) return false;

    const [day, month, year] = item.date.split("-");
    const itemMonth = `${month}-${year}`;

    return item.employeeId === employee.id && itemMonth === selectedMonth;
  });

  // Group work data by date
  const groupByDate = () => {
    const grouped = {};
    filteredDailyWork.forEach((item) => {
      if (!grouped[item.date]) {
        grouped[item.date] = [];
      }
      grouped[item.date].push(item);
    });
    return grouped;
  };

  const groupedData = groupByDate();

  // Get all days in the selected month
  const getAllDaysInMonth = () => {
    if (!selectedMonth) return [];

    const [month, year] = selectedMonth.split("-");
    const firstDay = moment(`${year}-${month}-01`, "YYYY-MM-DD");
    const daysInMonth = firstDay.daysInMonth();

    const allDays = [];
    for (let day = 1; day <= daysInMonth; day++) {
      const dayStr = String(day).padStart(2, "0");
      const dateStr = `${dayStr}-${month}-${year}`;
      allDays.push(dateStr);
    }

    return allDays;
  };

  const allDaysInMonth = getAllDaysInMonth();

  // Calculate daily totals
  const getDayTotal = (date) => {
    const dayData = groupedData[date] || [];
    return dayData.reduce(
      (sum, item) => sum + (parseFloat(item.salary) || 0),
      0,
    );
  };

  // Calculate month statistics
  const workingDays = Object.keys(groupedData).length;
  const totalSalary = isMonthlyEmployee
    ? workingDays > 0
      ? employeeSalary
      : 0
    : filteredDailyWork.reduce(
        (sum, item) => sum + (parseFloat(item.salary) || 0),
        0,
      );
  const avgDailySalary =
    workingDays > 0
      ? isMonthlyEmployee
        ? employeeSalary / workingDays
        : totalSalary / workingDays
      : 0;

  // Get place name
  const getPlaceName = (placeId) => {
    const place = places.find((p) => p.id === placeId);
    return place?.place || `${t("place")} ${placeId}`;
  };

  // Get manager name
  const getManagerName = (managerId) => {
    const manager = allEmployees.find((e) => e.id === managerId);
    return manager?.name || t("me");
  };

  // Get translated day name
  const getTranslatedDayName = (date) => {
    const dayIndex = moment(date, "DD-MM-YYYY").day();
    const dayKeys = [
      "sunday",
      "monday",
      "tuesday",
      "wednesday",
      "thursday",
      "friday",
      "saturday",
    ];
    return t(dayKeys[dayIndex]);
  };

  // Generate HTML for PDF Export
  const generateReportHTML = () => {
    const monthName = moment(selectedMonth, "MM-YYYY").format("MMMM YYYY");

    let workDaysHTML = "";
    allDaysInMonth.forEach((date) => {
      const dayData = groupedData[date] || [];
      if (dayData.length > 0) {
        const dayTotal = getDayTotal(date);
        const place = getPlaceName(dayData[0].placeId);
        const manager = dayData[0].whoMake
          ? getManagerName(dayData[0].whoMake)
          : "-";
        const notes =
          dayData
            .map((d) => d.note)
            .filter((n) => n && n.trim() !== "")
            .join(", ") || "-";

        workDaysHTML += `
          <tr>
            <td style="padding: 12px; border-bottom: 1px solid #E5E7EB;">${moment(
              date,
              "DD-MM-YYYY",
            ).format("DD")}</td>
            <td style="padding: 12px; border-bottom: 1px solid #E5E7EB;">${getTranslatedDayName(
              date,
            )}</td>
            <td style="padding: 12px; border-bottom: 1px solid #E5E7EB;">${place}</td>
            <td style="padding: 12px; border-bottom: 1px solid #E5E7EB;">${
              !isMonthlyEmployee ? `₪${dayTotal.toFixed(2)}` : t("worked")
            }</td>
            <td style="padding: 12px; border-bottom: 1px solid #E5E7EB;">${manager}</td>
            <td style="padding: 12px; border-bottom: 1px solid #E5E7EB; max-width: 200px; word-wrap: break-word;">${notes}</td>
          </tr>
        `;
      }
    });

    return `
      <!DOCTYPE html>
      <html dir="${isRTL ? "rtl" : "ltr"}">
        <head>
          <meta charset="utf-8">
          <meta name="viewport" content="width=device-width, initial-scale=1.0">
          <link href="https://fonts.googleapis.com/css2?family=Noto+Sans+Arabic:wght@400;600;700&family=Noto+Sans+Hebrew:wght@400;600;700&display=swap" rel="stylesheet">
          <style>
            @media print {
              @page { 
                size: A4; 
                margin: 15mm; 
              }
              body { 
                margin: 0; 
                padding: 0; 
              }
            }
            body {
              font-family: ${
                isRTL
                  ? "'Noto Sans Arabic', 'Noto Sans Hebrew', Arial, sans-serif"
                  : "Arial, sans-serif"
              };
              padding: 40px;
              direction: ${isRTL ? "rtl" : "ltr"};
              background: white;
              line-height: 1.6;
            }
            .header {
              text-align: center;
              margin-bottom: 40px;
              border-bottom: 3px solid #3B82F6;
              padding-bottom: 20px;
            }
            .header h1 {
              color: #3B82F6;
              margin: 0;
              font-size: 32px;
            }
            .header h2 {
              color: #6B7280;
              margin: 10px 0 0 0;
              font-size: 18px;
              font-weight: normal;
            }
            .contractor-info {
              background: #EFF6FF;
              padding: 15px 20px;
              border-radius: 12px;
              margin-bottom: 20px;
              border-left: 4px solid #3B82F6;
            }
            .contractor-name {
              font-size: 16px;
              font-weight: bold;
              color: #1E40AF;
            }
            .employee-info {
              background: #F9FAFB;
              padding: 20px;
              border-radius: 12px;
              margin-bottom: 30px;
              border: 1px solid #E5E7EB;
            }
            .info-row {
              display: flex;
              justify-content: space-between;
              margin-bottom: 10px;
            }
            .info-label {
              color: #6B7280;
              font-weight: 600;
            }
            .info-value {
              color: #111827;
              font-weight: bold;
            }
            .summary-cards {
              display: flex;
              gap: 20px;
              margin-bottom: 30px;
            }
            .summary-card {
              flex: 1;
              background: #FFFFFF;
              border: 2px solid #E5E7EB;
              border-radius: 12px;
              padding: 20px;
              text-align: center;
            }
            .summary-card.blue { border-color: #3B82F6; }
            .summary-card.green { border-color: #10B981; }
            .summary-card.purple { border-color: #8B5CF6; }
            .summary-label {
              color: #6B7280;
              font-size: 14px;
              margin-bottom: 8px;
            }
            .summary-value {
              color: #111827;
              font-size: 24px;
              font-weight: bold;
            }
            .work-table {
              width: 100%;
              border-collapse: collapse;
              background: #FFFFFF;
              border-radius: 12px;
              overflow: hidden;
              box-shadow: 0 1px 3px rgba(0,0,0,0.1);
            }
            .work-table th {
              background: #3B82F6;
              color: #FFFFFF;
              padding: 12px;
              text-align: ${isRTL ? "right" : "left"};
              font-weight: 600;
            }
            .work-table td {
              padding: 12px;
              border-bottom: 1px solid #E5E7EB;
              text-align: ${isRTL ? "right" : "left"};
            }
            .work-table tr:last-child td {
              border-bottom: none;
            }
            .section-title {
              font-size: 20px;
              font-weight: bold;
              color: #111827;
              margin-bottom: 16px;
              padding-bottom: 8px;
              border-bottom: 2px solid #E5E7EB;
            }
            .footer {
              text-align: center;
              margin-top: 40px;
              padding-top: 20px;
              border-top: 1px solid #E5E7EB;
              color: #6B7280;
              font-size: 12px;
            }
          </style>
        </head>
        <body>
          <div class="header">
            <h1>${t("workSlip")}</h1>
            <h2>${employee?.name} - ${monthName}</h2>
          </div>

          <div class="contractor-info">
            <div class="contractor-name">${t(
              "contractor",
            )}: ${contractorName}</div>
          </div>

          <div class="employee-info">
            <div class="info-row">
              <span class="info-label">${t("employeeName")}:</span>
              <span class="info-value">${employee?.name}</span>
            </div>
            <div class="info-row">
              <span class="info-label">${t("salaryType")}:</span>
              <span class="info-value">${
                isMonthlyEmployee ? t("monthly") : t("daily")
              }</span>
            </div>
            <div class="info-row">
              <span class="info-label">${
                isMonthlyEmployee ? t("monthlySalary") : t("dailySalary")
              }:</span>
              <span class="info-value">₪${employeeSalary.toLocaleString()}</span>
            </div>
          </div>

          <div class="summary-cards">
            <div class="summary-card blue">
              <div class="summary-label">${t("workingDays")}</div>
              <div class="summary-value">${workingDays}</div>
            </div>
            <div class="summary-card green">
              <div class="summary-label">${
                isMonthlyEmployee ? t("monthlySalary") : t("totalSalaryMonth")
              }</div>
              <div class="summary-value">₪${totalSalary.toLocaleString()}</div>
            </div>
            <div class="summary-card purple">
              <div class="summary-label">${
                isMonthlyEmployee ? t("avgPerDay") : t("avgDaily")
              }</div>
              <div class="summary-value">₪${avgDailySalary.toFixed(0)}</div>
            </div>
          </div>

          <div class="section-title">${t("workDays")}</div>
          
          ${
            workingDays > 0
              ? `
            <table class="work-table">
              <thead>
                <tr>
                  <th>${t("day")}</th>
                  <th>${t("dayName")}</th>
                  <th>${t("place")}</th>
                  <th>${t("salary")}</th>
                  <th>${t("manager")}</th>
                  <th>${t("notes")}</th>
                </tr>
              </thead>
              <tbody>
                ${workDaysHTML}
              </tbody>
            </table>
          `
              : `
            <p style="text-align: center; color: #6B7280; padding: 40px;">
              ${t("noWorkDataForMonth")}
            </p>
          `
          }

          <div class="footer">
            ${t(
              "generatedOn",
            )} ${new Date().toLocaleDateString()} ${new Date().toLocaleTimeString()}
          </div>
          
          <script>
            // Auto print when loaded
            window.onload = function() {
              window.print();
            };
          </script>
        </body>
      </html>
    `;
  };

  // Export as PDF (using window.print)
  const exportToPDF = () => {
    const htmlContent = generateReportHTML();

    // Open in new window and trigger print
    const printWindow = window.open("", "_blank");
    printWindow.document.write(htmlContent);
    printWindow.document.close();

    setExportModalVisible(false);
  };

  // Export as Excel
  const exportToExcel = () => {
    try {
      setIsExporting(true);

      const monthName = moment(selectedMonth, "MM-YYYY").format("MMMM YYYY");

      // Prepare data for Excel
      const excelData = [
        [t("workSlip"), ""],
        [`${employee?.name} - ${monthName}`, ""],
        [`${t("contractor")}: ${contractorName}`, ""],
        ["", ""],
        [t("employeeInformation"), ""],
        [t("employeeName"), employee?.name],
        [t("salaryType"), isMonthlyEmployee ? t("monthly") : t("daily")],
        [
          isMonthlyEmployee ? t("monthlySalary") : t("dailySalary"),
          `₪${employeeSalary.toLocaleString()}`,
        ],
        ["", ""],
        [t("summary"), ""],
        [t("workingDays"), workingDays],
        [
          isMonthlyEmployee ? t("monthlySalary") : t("totalSalaryMonth"),
          `₪${totalSalary.toFixed(2)}`,
        ],
        [
          isMonthlyEmployee ? t("avgPerDay") : t("avgDaily"),
          `₪${avgDailySalary.toFixed(2)}`,
        ],
        ["", ""],
        [t("workDays"), ""],
        [
          t("day"),
          t("dayName"),
          t("place"),
          t("salary"),
          t("manager"),
          t("notes"),
        ],
      ];

      // Add work days data
      allDaysInMonth.forEach((date) => {
        const dayData = groupedData[date] || [];
        if (dayData.length > 0) {
          const dayTotal = getDayTotal(date);
          const place = getPlaceName(dayData[0].placeId);
          const manager = dayData[0].whoMake
            ? getManagerName(dayData[0].whoMake)
            : "-";
          const notes =
            dayData
              .map((d) => d.note)
              .filter((n) => n && n.trim() !== "")
              .join(", ") || "-";

          excelData.push([
            moment(date, "DD-MM-YYYY").format("DD"),
            getTranslatedDayName(date),
            place,
            !isMonthlyEmployee ? `₪${dayTotal.toFixed(2)}` : t("worked"),
            manager,
            notes,
          ]);
        }
      });

      // Create workbook and worksheet
      const ws = XLSX.utils.aoa_to_sheet(excelData);

      // Set column widths
      ws["!cols"] = [
        { wch: 15 },
        { wch: 15 },
        { wch: 20 },
        { wch: 15 },
        { wch: 15 },
        { wch: 30 },
      ];

      // Create workbook
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, monthName);

      // Download Excel file
      XLSX.writeFile(
        wb,
        `${employee?.name}_${selectedMonth.replace("/", "-")}.xlsx`,
      );

      setExportModalVisible(false);
      alert(t("excelExportedSuccessfully"));
    } catch (error) {
      console.error("Error exporting Excel:", error);
      alert(t("failedToExportExcel"));
    } finally {
      setIsExporting(false);
    }
  };

  // Show export options
  const showExportOptions = () => {
    if (!selectedMonth) {
      alert(t("pleaseSelectMonth"));
      return;
    }
    setExportModalVisible(true);
  };

  return (
    <>
      <style>
        {`
          @keyframes fadeIn {
            from { opacity: 0; }
            to { opacity: 1; }
          }
          
          @keyframes slideUp {
            from {
              transform: translateY(100%);
              opacity: 0;
            }
            to {
              transform: translateY(0);
              opacity: 1;
            }
          }
        `}
      </style>

      {/* Main Modal */}
      <div
        style={{
          position: "fixed",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: "rgba(0, 0, 0, 0.5)",
          display: "flex",
          justifyContent: "center",
          alignItems: "flex-end",
          zIndex: 10000,
          animation: "fadeIn 0.2s ease-out",
        }}
        onClick={onClose}
      >
        <div
          style={{
            backgroundColor: "#FFFFFF",
            borderTopLeftRadius: "24px",
            borderTopRightRadius: "24px",
            width: "100%",
            maxWidth: "1000px",
            maxHeight: "90vh",
            display: "flex",
            flexDirection: "column",
            animation: "slideUp 0.3s ease-out",
            direction: isRTL ? "rtl" : "ltr",
          }}
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              padding: "20px",
              borderBottom: "1px solid #E5E7EB",
              flexDirection: isRTL ? "row-reverse" : "row",
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "12px",
                flexDirection: isRTL ? "row-reverse" : "row",
              }}
            >
              <div
                style={{
                  width: "48px",
                  height: "48px",
                  borderRadius: "24px",
                  backgroundColor: "#3B82F6",
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                }}
              >
                <span
                  style={{
                    fontSize: "20px",
                    fontWeight: "bold",
                    color: "#FFFFFF",
                  }}
                >
                  {employee?.name?.charAt(0).toUpperCase()}
                </span>
              </div>
              <div style={{ textAlign: isRTL ? "right" : "left" }}>
                <h2
                  style={{
                    fontSize: "18px",
                    fontWeight: "700",
                    color: "#111827",
                    margin: 0,
                  }}
                >
                  {employee?.name}
                </h2>
                <p
                  style={{
                    fontSize: "13px",
                    color: "#6B7280",
                    margin: "2px 0 0 0",
                  }}
                >
                  {isMonthlyEmployee ? t("monthly") : t("daily")} - ₪
                  {employeeSalary.toLocaleString()}
                </p>
              </div>
            </div>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "8px",
                flexDirection: isRTL ? "row-reverse" : "row",
              }}
            >
              {/* Export Button */}
              <button
                onClick={showExportOptions}
                disabled={isExporting}
                style={{
                  width: "36px",
                  height: "36px",
                  borderRadius: "18px",
                  backgroundColor: "#D1FAE5",
                  border: "1px solid #A7F3D0",
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  cursor: isExporting ? "not-allowed" : "pointer",
                  opacity: isExporting ? 0.5 : 1,
                }}
              >
                <Download size={20} color="#10B981" />
              </button>
              {/* Close Button */}
              <button
                onClick={onClose}
                style={{
                  width: "36px",
                  height: "36px",
                  borderRadius: "18px",
                  backgroundColor: "#F3F4F6",
                  border: "none",
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  cursor: "pointer",
                }}
              >
                <X size={24} color="#6B7280" />
              </button>
            </div>
          </div>

          {/* Scrollable Content */}
          <div
            style={{
              flex: 1,
              overflowY: "auto",
              padding: "20px",
            }}
          >
            {/* Month Selector */}
            <div style={{ marginBottom: "20px" }}>
              <h3
                style={{
                  fontSize: "16px",
                  fontWeight: "700",
                  color: "#111827",
                  marginBottom: "12px",
                  textAlign: isRTL ? "right" : "left",
                }}
              >
                {t("selectMonth")}
              </h3>
              <div
                style={{
                  display: "flex",
                  gap: "12px",
                  overflowX: "auto",
                  paddingBottom: "8px",
                  flexDirection: isRTL ? "row-reverse" : "row",
                }}
              >
                {monthList.map((item) => {
                  const [month, year] = item.split("-");
                  const monthName = moment(item, "MM-YYYY").format("MMM");
                  const isSelected = selectedMonth === item;

                  return (
                    <button
                      key={item}
                      onClick={() => setSelectedMonth(item)}
                      style={{
                        padding: "10px 16px",
                        borderRadius: "12px",
                        backgroundColor: isSelected ? "#3B82F6" : "#FFFFFF",
                        border: `2px solid ${
                          isSelected ? "#3B82F6" : "#E5E7EB"
                        }`,
                        minWidth: "70px",
                        textAlign: "center",
                        cursor: "pointer",
                        transition: "all 0.2s",
                      }}
                    >
                      <div
                        style={{
                          fontSize: "14px",
                          fontWeight: "600",
                          color: isSelected ? "#FFFFFF" : "#6B7280",
                        }}
                      >
                        {monthName}
                      </div>
                      <div
                        style={{
                          fontSize: "11px",
                          color: isSelected ? "#DBEAFE" : "#9CA3AF",
                        }}
                      >
                        {year}
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Summary Cards */}
            <div
              style={{
                display: "flex",
                gap: "10px",
                marginBottom: "20px",
                flexWrap: "wrap",
              }}
            >
              <div
                style={{
                  flex: 1,
                  minWidth: "150px",
                  backgroundColor: "#FAFBFF",
                  borderRadius: "12px",
                  padding: "16px",
                  border: "1px solid #E0E7FF",
                  borderBottom: "3px solid #3B82F6",
                  textAlign: "center",
                }}
              >
                <div
                  style={{
                    width: "32px",
                    height: "32px",
                    borderRadius: "16px",
                    backgroundColor: "#FFFFFF",
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    margin: "0 auto 8px",
                    boxShadow: "0 1px 2px rgba(0,0,0,0.1)",
                  }}
                >
                  <Calendar size={18} color="#3B82F6" />
                </div>
                <div
                  style={{
                    fontSize: "20px",
                    fontWeight: "700",
                    color: "#111827",
                    marginBottom: "4px",
                  }}
                >
                  {workingDays}
                </div>
                <div
                  style={{
                    fontSize: "11px",
                    color: "#6B7280",
                    fontWeight: "500",
                  }}
                >
                  {t("workingDays")}
                </div>
              </div>

              <div
                style={{
                  flex: 1,
                  minWidth: "150px",
                  backgroundColor: "#F7FEF9",
                  borderRadius: "12px",
                  padding: "16px",
                  border: "1px solid #D1FAE5",
                  borderBottom: "3px solid #10B981",
                  textAlign: "center",
                }}
              >
                <div
                  style={{
                    width: "32px",
                    height: "32px",
                    borderRadius: "16px",
                    backgroundColor: "#FFFFFF",
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    margin: "0 auto 8px",
                    boxShadow: "0 1px 2px rgba(0,0,0,0.1)",
                  }}
                >
                  <Wallet size={18} color="#10B981" />
                </div>
                <div
                  style={{
                    fontSize: "20px",
                    fontWeight: "700",
                    color: "#111827",
                    marginBottom: "4px",
                  }}
                >
                  ₪{totalSalary.toLocaleString()}
                </div>
                <div
                  style={{
                    fontSize: "11px",
                    color: "#6B7280",
                    fontWeight: "500",
                  }}
                >
                  {isMonthlyEmployee
                    ? t("monthlySalary")
                    : t("totalSalaryMonth")}
                </div>
              </div>

              <div
                style={{
                  flex: 1,
                  minWidth: "150px",
                  backgroundColor: "#FDFAFF",
                  borderRadius: "12px",
                  padding: "16px",
                  border: "1px solid #F3E8FF",
                  borderBottom: "3px solid #8B5CF6",
                  textAlign: "center",
                }}
              >
                <div
                  style={{
                    width: "32px",
                    height: "32px",
                    borderRadius: "16px",
                    backgroundColor: "#FFFFFF",
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    margin: "0 auto 8px",
                    boxShadow: "0 1px 2px rgba(0,0,0,0.1)",
                  }}
                >
                  <TrendingUp size={18} color="#8B5CF6" />
                </div>
                <div
                  style={{
                    fontSize: "20px",
                    fontWeight: "700",
                    color: "#111827",
                    marginBottom: "4px",
                  }}
                >
                  ₪{avgDailySalary.toFixed(0)}
                </div>
                <div
                  style={{
                    fontSize: "11px",
                    color: "#6B7280",
                    fontWeight: "500",
                  }}
                >
                  {isMonthlyEmployee ? t("avgPerDay") : t("avgDaily")}
                </div>
              </div>
            </div>

            {/* Work Days Grid */}
            <div>
              <h3
                style={{
                  fontSize: "16px",
                  fontWeight: "700",
                  color: "#111827",
                  marginBottom: "16px",
                  textAlign: isRTL ? "right" : "left",
                }}
              >
                {t("workDays")}
              </h3>

              {workingDays === 0 ? (
                <div
                  style={{
                    textAlign: "center",
                    padding: "60px 20px",
                  }}
                >
                  <Calendar size={48} color="#D1D5DB" />
                  <p
                    style={{
                      fontSize: "14px",
                      color: "#9CA3AF",
                      marginTop: "12px",
                    }}
                  >
                    {t("noWorkDataForMonth")}
                  </p>
                </div>
              ) : (
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns:
                      "repeat(auto-fill, minmax(180px, 1fr))",
                    gap: "12px",
                  }}
                >
                  {allDaysInMonth.map((date) => {
                    const dayData = groupedData[date] || [];
                    const hasWork = dayData.length > 0;
                    const dayTotal = getDayTotal(date);

                    if (!hasWork) return null;

                    return (
                      <div
                        key={date}
                        style={{
                          backgroundColor: "#FFFFFF",
                          borderRadius: "12px",
                          padding: "12px",
                          border: "2px solid #10B981",
                          boxShadow: "0 1px 3px rgba(0,0,0,0.05)",
                        }}
                      >
                        {/* Day Header */}
                        <div
                          style={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            marginBottom: "8px",
                            flexDirection: isRTL ? "row-reverse" : "row",
                          }}
                        >
                          <span
                            style={{
                              fontSize: "18px",
                              fontWeight: "bold",
                              color: "#10B981",
                            }}
                          >
                            {moment(date, "DD-MM-YYYY").format("DD")}
                          </span>
                          <span
                            style={{
                              fontSize: "10px",
                              fontWeight: "600",
                              color: "#6B7280",
                              textTransform: "uppercase",
                            }}
                          >
                            {getTranslatedDayName(date)}
                          </span>
                        </div>

                        {/* Place */}
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "6px",
                            marginBottom: "8px",
                            padding: "6px",
                            backgroundColor: "#F9FAFB",
                            borderRadius: "8px",
                            flexDirection: isRTL ? "row-reverse" : "row",
                          }}
                        >
                          <MapPin size={14} color="#10B981" />
                          <span
                            style={{
                              fontSize: "11px",
                              fontWeight: "600",
                              color: "#111827",
                              flex: 1,
                              whiteSpace: "nowrap",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              textAlign: isRTL ? "right" : "left",
                            }}
                          >
                            {getPlaceName(dayData[0].placeId)}
                          </span>
                        </div>

                        {/* Salary Badge */}
                        {!isMonthlyEmployee ? (
                          <div
                            style={{
                              backgroundColor: "#FEE2E2",
                              padding: "4px 8px",
                              borderRadius: "6px",
                              display: "inline-block",
                            }}
                          >
                            <span
                              style={{
                                fontSize: "13px",
                                fontWeight: "bold",
                                color: "#DC2626",
                              }}
                            >
                              ₪{dayTotal.toFixed(0)}
                            </span>
                          </div>
                        ) : (
                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: "4px",
                              backgroundColor: "#F3E8FF",
                              padding: "4px 8px",
                              borderRadius: "6px",
                              display: "inline-flex",
                              flexDirection: isRTL ? "row-reverse" : "row",
                            }}
                          >
                            <CheckCircle size={14} color="#9C27B0" />
                            <span
                              style={{
                                fontSize: "11px",
                                fontWeight: "600",
                                color: "#9C27B0",
                              }}
                            >
                              {t("worked")}
                            </span>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Export Options Modal */}
      {exportModalVisible && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(0, 0, 0, 0.5)",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            zIndex: 10001,
            animation: "fadeIn 0.2s ease-out",
          }}
          onClick={() => setExportModalVisible(false)}
        >
          <div
            style={{
              backgroundColor: "#FFFFFF",
              borderRadius: "20px",
              padding: "32px",
              maxWidth: "450px",
              width: "90%",
              boxShadow: "0 20px 60px rgba(0, 0, 0, 0.3)",
              animation: "slideUp 0.3s ease-out",
              direction: isRTL ? "rtl" : "ltr",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                marginBottom: "24px",
                flexDirection: isRTL ? "row-reverse" : "row",
              }}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "12px",
                  flexDirection: isRTL ? "row-reverse" : "row",
                }}
              >
                <div
                  style={{
                    width: "56px",
                    height: "56px",
                    borderRadius: "14px",
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    backgroundColor: "#3B82F615",
                  }}
                >
                  <Download size={32} color="#3B82F6" />
                </div>
                <div style={{ textAlign: isRTL ? "right" : "left" }}>
                  <h2
                    style={{
                      margin: 0,
                      fontSize: "22px",
                      fontWeight: 700,
                      color: "#1F2937",
                    }}
                  >
                    {t("exportWorkSlip")}
                  </h2>
                  <p
                    style={{
                      margin: "4px 0 0 0",
                      fontSize: "14px",
                      color: "#6B7280",
                    }}
                  >
                    {t("chooseExportFormat")}
                  </p>
                </div>
              </div>
              <button
                onClick={() => setExportModalVisible(false)}
                style={{
                  background: "none",
                  border: "none",
                  cursor: "pointer",
                  padding: "8px",
                  borderRadius: "8px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  transition: "background-color 0.2s",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = "#F3F4F6";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = "transparent";
                }}
              >
                <X size={24} color="#6B7280" />
              </button>
            </div>

            {/* Export Options */}
            <div
              style={{ display: "flex", flexDirection: "column", gap: "12px" }}
            >
              {/* Excel Option */}
              <button
                onClick={exportToExcel}
                style={{
                  padding: "20px",
                  borderRadius: "14px",
                  border: "2px solid #E5E7EB",
                  backgroundColor: "#FFFFFF",
                  cursor: "pointer",
                  transition: "all 0.2s",
                  display: "flex",
                  alignItems: "center",
                  gap: "16px",
                  flexDirection: isRTL ? "row-reverse" : "row",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.borderColor = "#10B981";
                  e.currentTarget.style.backgroundColor = "#10B98108";
                  e.currentTarget.style.transform = "translateY(-2px)";
                  e.currentTarget.style.boxShadow =
                    "0 8px 16px rgba(16, 185, 129, 0.15)";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.borderColor = "#E5E7EB";
                  e.currentTarget.style.backgroundColor = "#FFFFFF";
                  e.currentTarget.style.transform = "translateY(0)";
                  e.currentTarget.style.boxShadow = "none";
                }}
              >
                <div
                  style={{
                    width: "52px",
                    height: "52px",
                    borderRadius: "12px",
                    backgroundColor: "#10B98115",
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    flexShrink: 0,
                  }}
                >
                  <FileText size={28} color="#10B981" />
                </div>
                <div style={{ flex: 1, textAlign: isRTL ? "right" : "left" }}>
                  <h3
                    style={{
                      margin: 0,
                      fontSize: "18px",
                      fontWeight: 600,
                      color: "#1F2937",
                    }}
                  >
                    Excel (XLSX)
                  </h3>
                  <p
                    style={{
                      margin: "4px 0 0 0",
                      fontSize: "13px",
                      color: "#6B7280",
                    }}
                  >
                    {t("excelDescription")}
                  </p>
                </div>
                <ChevronRight
                  size={24}
                  color="#9CA3AF"
                  style={{
                    transform: isRTL ? "rotate(180deg)" : "none",
                  }}
                />
              </button>

              {/* PDF Option */}
              <button
                onClick={exportToPDF}
                style={{
                  padding: "20px",
                  borderRadius: "14px",
                  border: "2px solid #E5E7EB",
                  backgroundColor: "#FFFFFF",
                  cursor: "pointer",
                  transition: "all 0.2s",
                  display: "flex",
                  alignItems: "center",
                  gap: "16px",
                  flexDirection: isRTL ? "row-reverse" : "row",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.borderColor = "#EF4444";
                  e.currentTarget.style.backgroundColor = "#EF444408";
                  e.currentTarget.style.transform = "translateY(-2px)";
                  e.currentTarget.style.boxShadow =
                    "0 8px 16px rgba(239, 68, 68, 0.15)";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.borderColor = "#E5E7EB";
                  e.currentTarget.style.backgroundColor = "#FFFFFF";
                  e.currentTarget.style.transform = "translateY(0)";
                  e.currentTarget.style.boxShadow = "none";
                }}
              >
                <div
                  style={{
                    width: "52px",
                    height: "52px",
                    borderRadius: "12px",
                    backgroundColor: "#EF444415",
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    flexShrink: 0,
                  }}
                >
                  <FileText size={28} color="#EF4444" />
                </div>
                <div style={{ flex: 1, textAlign: isRTL ? "right" : "left" }}>
                  <h3
                    style={{
                      margin: 0,
                      fontSize: "18px",
                      fontWeight: 600,
                      color: "#1F2937",
                    }}
                  >
                    PDF
                  </h3>
                  <p
                    style={{
                      margin: "4px 0 0 0",
                      fontSize: "13px",
                      color: "#6B7280",
                    }}
                  >
                    {t("pdfDescription")}
                  </p>
                </div>
                <ChevronRight
                  size={24}
                  color="#9CA3AF"
                  style={{
                    transform: isRTL ? "rotate(180deg)" : "none",
                  }}
                />
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
